import { CSSResultGroup, html, nothing, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { OutlineButton } from '../../base/outline-button/outline-button';
import '../ghs-icon/ghs-icon';
import componentStyles from './ghs-button.css.lit';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'quaternary'
  | 'cta-dark'
  | 'cta-light'
  | 'chip';
export type BorderVariant =
  | 'white'
  | 'blue'
  | 'magenta'
  | 'green'
  | 'square'
  | 'none';
export type TextVariant = 'white' | 'blue' | 'magenta' | 'green';
export type ButtonSize = 'small' | 'medium' | 'large' | 'xl';
export type Justify = 'center' | 'between';
export type iconPosition = 'left' | 'right' | 'top';
export type ContainerFitVariant = true | false;

/**
 * The GHS Button component
 * @slot - The default, and only slot for this element.
 */
@customElement('ghs-button')
export class GhsButton extends OutlineButton {
  static styles: CSSResultGroup = [componentStyles];

  /**
   * Optional param. If set renders a button-styled link.
   */
  @property({ type: String })
  url: string;

  /**
   * Size
   * @param size
   * Medium was added for chips, we don't have medium sizes for regular buttons.
   */
  @property({ type: String, attribute: 'size' })
  size: ButtonSize = 'large';

  /**
   * Icon name for ghs-icon component
   * @param icon
   */
  @property({ type: String, attribute: 'icon' })
  icon: string;

  /**
   * Animates the icon on hover
   * @param animateIcon boolean
   * @default true
   */
  @property({ type: Boolean, attribute: 'animate-icon', reflect: true })
  animateIcon = true;

  /**
   * Switches ghs-button to hover state
   * @param isHovered boolean
   * @default false
   */
  @property({ type: Boolean, attribute: 'is-hovered', reflect: true })
  isHovered = false;
  /**
   * Whether or not the icon is placed on the left or right of the button text
   * @param iconPosition
   * @default 'right'
   *
   */
  @property({ type: String, attribute: 'icon-position', reflect: true })
  iconPosition: iconPosition = 'right';

  /**
   * The size of the icon rendered, provided as a number in pixels
   * @param iconSize
   * @default null
   *
   */
  @property({ type: String, attribute: 'icon-size', reflect: true })
  iconSize = null;

  @property({ type: String, attribute: 'variant' })
  variant: ButtonVariant = 'primary';

  /**
   * The button text style variant to use.
   * @param textVariant
   */
  @property({ type: String, attribute: 'text-variant' })
  textVariant: TextVariant;

  /**
   * The button border style variant to use.
   * @param borderVariant
   */
  @property({ type: String, attribute: 'border-variant' })
  borderVariant: BorderVariant;

  /**
   * Container size flexibility for the button; button fills
   * container or is self sizing. Default behavior is false.
   * Applies to both button and CTA versions.
   * @param containerFitVariant
   * @default false
   */
  @property({
    type: Boolean,
    attribute: 'container-fit-variant',
    reflect: true,
  })
  containerFitVariant: ContainerFitVariant = false;

  /**
   * Container size flexibility for the button; button fills
   * container or is self sizing. Default behavior is false.
   * Applies to both button and CTA versions.
   * @param containerFitVariant
   * @default false
   */
  @property({
    type: String,
    attribute: 'justify-variant',
    reflect: true,
  })
  justifyVariant: Justify = 'center';

  /**
   * Sets the text for screen readers disabled buttons.
   * If your button can be disabled you must provide a short explanatory reason.
   * @param disabledInfo
   */
  @property({ type: String, attribute: 'disabled-info' })
  disabledInfo?: string | undefined;

  // Return the aria label description depending on the target.
  getTargetInfo() {
    if (ifDefined(this.buttonTarget) && this.buttonTarget === '_blank') {
      return 'This button will open in a new window';
    }
    return null;
  }

  iconTemplate = () => {
    return this.icon
      ? html` ${this.iconPosition === 'left' || this.iconPosition === 'top'
            ? html` <div class="${this.iconPosition}">
                <ghs-icon
                  name="${this.icon}"
                  mobile-size=${(this.iconSize ?? '14') + 'px'}
                  size=${(this.iconSize ?? '20') + 'px'}
                  site=${this.site}
                ></ghs-icon>
              </div>`
            : nothing}

          <slot></slot>

          ${this.iconPosition === 'right'
            ? html` <div class="right">
                <ghs-icon
                  name="${this.icon}"
                  mobile-size=${(this.iconSize ?? '13') + 'px'}
                  size=${(this.iconSize ?? '17') + 'px'}
                  site=${this.site}
                ></ghs-icon>
              </div>`
            : nothing}`
      : html`<slot></slot>`;
  };

  render(): TemplateResult {
    const classes = {
      'btn': true,
      [this.variant]: true,
      [`text-${this.textVariant}`]: this.textVariant,
      [`border-${this.borderVariant}`]: this.borderVariant,
      [`justify-${this.justifyVariant}`]: this.justifyVariant,
      'parent-container-fit': this.containerFitVariant,
      [this.size]: true,
    };

    return this.url !== undefined
      ? html`<a
          href="${this.url}"
          target=${ifDefined(this.buttonTarget)}
          rel=${ifDefined(this.buttonTarget && 'noreferrer noopener')}
          class=${classMap(classes)}
          aria-label=${ifDefined(this.getTargetInfo())}
        >
          ${this.icon ? this.iconTemplate() : html`<slot></slot>`}
        </a>`
      : html`<button
          class=${classMap(classes)}
          aria-disabled="${!!this.isDisabled}"
          .onclick="${this.onClick}"
          .onkeyup="${this.onKeyUp}"
          aria-label=${this.isDisabled
            ? ifDefined(this.disabledInfo)
            : ifDefined(null)}
        >
          ${this.icon ? this.iconTemplate() : html`<slot></slot>`}
        </button> `;
  }
}
